/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import {
  Box,
  Container,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  Button,
  Checkbox,
} from "@mui/material";
import * as yup from "yup";
import { useFormik } from "formik";
import dayjs from "dayjs";
import "dayjs/locale/es";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "@store/store";
import { setLoanStatus, setShareHolderRfcCurp } from "@store/slices/registerSlice";
import { useLazyGetCurpQuery } from "@api/onboarding";
import { usePatchPersonMutation } from "@api/personasApi";
import { usePostValidateNextStageMutation } from "@api/applications";
import { ProgressBar } from "@components/ProgressBar";
import { convertSex, extractRFCFromCurp } from "@helpers/funciones";
import { CURP, RFC_PFAE_SIN_CLAVE, RFC_HOMOCLAVE } from "@utils/expreciones";
import { CurpQuery } from "@interfaces/index";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { InactivityHandler } from "@components/InactivityHandler";
import { ApiError } from "@interfaces/response";
import CustomRightImage from "@components/CustomRightImagev2";
import * as Sentry from "@sentry/react";
import { CustomSwitch } from "@components/CustomSwitch";
import { CustomTextCiecBuro } from "@components/CustomTextCiecBuro";
import useInterfaceConfig from "@hooks/useInterfaceConfig";
import useStatusConfig from "@hooks/useStatusConfig";
import useTofuSteps from "@hooks/useTofuSteps";

const SCREEN_STATUS = "CSCDA";

const RfcCurpForm = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const interfaceConfig = useInterfaceConfig();
  const { nextStatus } = useStatusConfig(SCREEN_STATUS);
  const { numberOfSteps, currentStep } = useTofuSteps(SCREEN_STATUS);

  const application = useSelector((state: RootState) => state.register.application);
  const shareholder = useSelector((state: RootState) => state.register.shareholder);
  const accountUser = useSelector((state: RootState) => state.register.account_user);

  const [getCurp] = useLazyGetCurpQuery();
  const [patchPerson] = usePatchPersonMutation();
  const [triggerPostNextStage] = usePostValidateNextStageMutation();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const formik = useFormik({
    initialValues: {
      rfc: shareholder.rfc,
      homoclave: shareholder.rfc_homoclave,
      curp: shareholder.curp,
      confirmCheck: false,
      confirmAuthorization: false,
    },
    validationSchema: yup.object().shape({
      rfc: yup.string().required("Campo requerido").matches(RFC_PFAE_SIN_CLAVE, "Formato de RFC no válido"),
      homoclave: yup.string().required("Campo requerido").matches(RFC_HOMOCLAVE, "Formato de homoclave no válido"),
      curp: yup.string().required("Campo requerido").matches(CURP, "Formato de CURP no válido"),
      confirmCheck: yup.boolean().isTrue().required(""),
      confirmAuthorization: yup.boolean().isTrue().required(""),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      if (!formik.isValid) return;

      try {
        if (!nextStatus) {
          throw new Error("No se encontró el siguiente status");
        }

        setSubmitting(true);
        await patchPerson({
          personId: shareholder.id,
          body: {
            rfc: values.rfc + values.homoclave,
            curp: values.curp,
          },
        }).unwrap();

        dispatch(
          setShareHolderRfcCurp({
            rfc: values.rfc,
            rfc_homoclave: values.homoclave,
            curp: values.curp,
          }),
        );

        if (!shareholder.check_sat_bureau) {
          await triggerPostNextStage({
            applicationId: application.id,
            statusCode: nextStatus?.code,
            username: `TOFU - ${accountUser.email}`,
          }).unwrap();

          dispatch(setLoanStatus(nextStatus?.code));
        }

        navigate(nextStatus?.url);
      } catch (error) {
        Sentry.captureException(error, {
          extra: {
            component: "RfcCurp",
            values: formik.values,
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    const fetchCurp = async () => {
      try {
        setIsLoading(true);

        const birthStateNumber = Number(shareholder.birth_state);
        const birthStateFormatted = birthStateNumber.toString().padStart(2, "0");

        const payloadGetCurp: CurpQuery = {
          nombres: shareholder.name,
          apellido_paterno: shareholder.first_lastname,
          apellido_materno: shareholder.second_lastname,
          estado_nacimiento: birthStateFormatted,
          fecha_nacimiento: dayjs(shareholder.date_birth, "YYYY-MM-DD").format("DD-MM-YYYY"),
          sexo: convertSex(shareholder.sex),
        };
        const responseGetCurp = await getCurp(payloadGetCurp).unwrap();
        const rfc = extractRFCFromCurp(responseGetCurp.data.curp);
        formik.setFieldValue("curp", responseGetCurp.data.curp);
        formik.setFieldValue("rfc", rfc);
      } catch (error) {
        console.log("Hubo un error al obtener la CURP: ", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCurp();
  }, []);

  useEffect(() => {
    formik.setFieldValue("confirmCheck", shareholder.check_sat_bureau);
  }, [shareholder.check_sat_bureau]);

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8}>
        <InactivityHandler />
        <Box
          sx={{
            mt: isMobileOrTablet ? 2 : 4,
            mx: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography
            variant={isMobileOrTablet ? "h4" : "h3"}
            fontWeight={500}
            textAlign="center"
            color={theme?.palette?.primary?.dark}
          >
            {interfaceConfig.textos_tofu?.pm?.rfc_curp?.titulo || "Registro del accionista mayoritario"}
          </Typography>

          <Container maxWidth="sm" sx={{ mt: isMobileOrTablet ? 2 : 3 }}>
            <ProgressBar totalSteps={numberOfSteps} currentStep={currentStep} />
          </Container>
        </Box>

        <Container maxWidth="sm" sx={{ mt: isMobileOrTablet ? 2 : 3 }}>
          <form onSubmit={formik.handleSubmit} noValidate>
            <Grid container columnSpacing={2}>
              <Grid item xs={8}>
                <TextField
                  id="rfc"
                  name="rfc"
                  label="RFC accionista"
                  margin="normal"
                  required
                  fullWidth
                  autoComplete="off"
                  onChange={(e) => {
                    const input = e.target;
                    const value = e.target.value.toUpperCase();
                    const start = input.selectionStart;
                    formik.handleChange(e);
                    formik.setFieldValue("rfc", value);
                    setTimeout(() => {
                      input.setSelectionRange(start, start);
                    }, 0);
                  }}
                  inputProps={{ maxLength: 10 }}
                  onBlur={formik.handleBlur}
                  value={formik.values.rfc}
                  error={formik.touched.rfc && Boolean(formik.errors.rfc)}
                  helperText={formik.touched.rfc && formik.errors.rfc}
                  disabled={isLoading || formik.isSubmitting || shareholder.check_sat_bureau}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  id="homoclave"
                  name="homoclave"
                  label="Homoclave"
                  margin="normal"
                  required
                  fullWidth
                  autoComplete="off"
                  onChange={(e) => {
                    const input = e.target;
                    const value = e.target.value.toUpperCase();
                    const start = input.selectionStart;
                    formik.handleChange(e);
                    formik.setFieldValue("homoclave", value);
                    setTimeout(() => {
                      input.setSelectionRange(start, start);
                    }, 0);
                  }}
                  onBlur={formik.handleBlur}
                  value={formik.values.homoclave}
                  error={formik.touched.homoclave && Boolean(formik.errors.homoclave)}
                  helperText={formik.touched.homoclave && formik.errors.homoclave}
                  inputProps={{ maxLength: 3 }}
                  disabled={isLoading || formik.isSubmitting || shareholder.check_sat_bureau}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  id="curp"
                  name="curp"
                  label="CURP"
                  margin="normal"
                  required
                  fullWidth
                  autoComplete="off"
                  onChange={(e) => {
                    const input = e.target;
                    const value = e.target.value.toUpperCase();
                    const start = input.selectionStart;
                    formik.handleChange(e);
                    formik.setFieldValue("curp", value);
                    setTimeout(() => {
                      input.setSelectionRange(start, start);
                    }, 0);
                  }}
                  onBlur={formik.handleBlur}
                  value={formik.values.curp}
                  error={formik.touched.curp && Boolean(formik.errors.curp)}
                  helperText={formik.touched.curp && formik.errors.curp}
                  inputProps={{ maxLength: 18 }}
                  disabled={isLoading || formik.isSubmitting || shareholder.check_sat_bureau}
                />
              </Grid>
              <Grid item xs={12} mt={1}>
                <FormControlLabel
                  control={<Checkbox id="confirmCheck" name="confirmCheck" onChange={formik.handleChange} />}
                  label="Confirmo que el RFC y CURP son correctos"
                />
              </Grid>

              <Grid item xs={12} mt={2}>
                <FormControlLabel
                  control={<CustomSwitch />}
                  label={
                    <Typography variant={"body2"} sx={{ mt: 2, mb: 2 }} color={theme?.palette?.primary?.dark}>
                      <CustomTextCiecBuro type="confirmAval" />
                    </Typography>
                  }
                  name="confirmAuthorization"
                  checked={formik.values.confirmAuthorization}
                  onChange={formik.handleChange}
                  disabled={isLoading || formik.isSubmitting}
                />
              </Grid>
            </Grid>

            <Box display="flex" justifyContent="center" mt={isMobileOrTablet ? 2 : 3} mb={2}>
              <Button
                variant="default"
                type="submit"
                color="primary"
                disabled={!formik.isValid || isLoading || formik.isSubmitting}
              >
                Continuar
              </Button>
            </Box>
          </form>
        </Container>
      </Grid>

      {isMobileOrTablet == false && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default RfcCurpForm;
